import type { Option } from "../core/option.ts";
import { compilePathPattern } from "@tento-core/invalidation";
import { SheetRegistries } from "../sheet/sheet-registries.ts";
import { Rational } from "@tento-core/rational";
import { Tracing } from "@tento-core/tracing";

import type { CellValue } from "../core/dtype.ts";
import { RowKey } from "../ids/row-key.ts";
import { isRendererWireType } from "../render/renderer-kind.ts";
import type {
  ChildSpec,
  ChildSpecAttributes,
  RawRowInput,
} from "../rows/row-type-spec.ts";
import { PARENT_ROW_ID, RowTypeSpecOps } from "../rows/row-type-spec.ts";
import { Json, type JsonObject } from "../core/json.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
import { sheetInternals, sheetOf, sheetStores } from "../sheet/sheet-stores.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { SourceCells } from "../rows/variants/source-cells.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import { AliasRow, aliasWriterOf } from "../rows/variants/alias-row.ts";

/** The type of one alias prop. */
export type PropSchema = "string" | "number" | "obj" | "boolean";
/** An alias instance's props, the inputs its template binds. */
export type AliasProps = Readonly<Record<string, CellValue>>;

/**
 * A `DataRef` is a read-only handle to data the holder does not own — the
 * explicit form of the "Data Context" (see the data-address design note,
 * `lona-sdk-2.0.project.docs/content/data-address/`). A row is addressed one
 * of two ways, exclusively:
 *   - absolute `lookupKey` — a native source row; routes the read
 *     (`:` → sheet-local `sourceCellData`, `:~` → Client `canonicalCells`,
 *     fetched once and shared cross-tenant).
 *   - relative `localPath` — a generic `ComputedDataSource` row; its concrete
 *     key resolves to `nearestParent.lookupKey + localPath` (e.g. `localPath:
 *     "due"` under `:~lona:task` ⇒ `:~lona:task.due`), so the derived row is
 *     portable and concretely addressable. `localPath: null` ⇒ addressed by
 *     the row's own absolute `lookupKey`.
 * A series is its own materialized (formula-derived, read-only) row, not a
 * slice; reuse/dedup is the existing `canonicalCells` by concrete key.
 */
export interface DataRef {
  readonly lookupKey: string;
  readonly localPath: Option<string>;
}

/** An alias instance's device-local expansion state: which of its dynamic children are shown, by key. */
export interface AliasLocalState {
  readonly visibleByKey: ReadonlyMap<string, boolean>;
}

/** What an alias template's rule reads when it expands: the alias row's props and the sheet it sits in. */
export interface AliasTemplateContext {
  readonly timescale: string;
  /** The expanding sheet's lookup tables (`Sheet.registries`): data presets, row types. */
  readonly registries: SheetRegistries;
  /**
   * The alias row's top-level attribute bag, so a template's
   * `rule.children` can gate hydration on instance options (e.g.
   * calendar's `hideAllDayEvents` suppressing the all-day projection).
   */
  readonly attributes: JsonObject;
}

/** An alias template rule that expands to a fixed list of child specs. */
export type StaticRule = {
  readonly kind: "static";
  readonly children:
    | readonly ChildSpec[]
    | ((props: AliasProps, ctx: AliasTemplateContext) => readonly ChildSpec[]);
};

/**
 * The child rows an alias template produces from data at expansion time,
 * after its static children.
 */
export type DynamicTail = {
  /**
   * Static child preset to attach the dynamic items under. Omit to
   * attach directly under the alias root — useful for client-hydrated
   * aliases whose static rule has no children (R6.2). When set, the
   * referenced preset must exist as a static child of the alias.
   */
  readonly parentPreset?: string;
  readonly source?: string;
  readonly items?: (props: AliasProps, sheet: ModelSheet) => readonly CellValue[];
  readonly as: (item: CellValue, props: AliasProps, index: number) => ChildSpec;
  readonly filter?: (
    item: CellValue,
    localState: AliasLocalState,
    index: number,
  ) => boolean;
};

/**
 * A template an alias row expands: its display name, props and the child rows
 * it produces.
 */
export interface AliasTemplate {
  /**
   * Human-readable name for instances of this template, used as the
   * display label for alias rows that carry no explicit `label`
   * (e.g. the sheet property panel). Each template is self-describing
   * — when omitted, consumers fall back to a title-cased form of the
   * template id (see `AliasTemplateOps.displayName`).
   */
  readonly displayName?: string;
  readonly props?: Readonly<Record<string, PropSchema>>;
  readonly rule: StaticRule;
  /**
   * The alias's **default data address**: where querying this alias
   * resolves to find its cells. The alias keeps its own identity
   * (`:~lona:weather`, per-user) while declaring that its data lives at
   * the resolved source (`:~weather:{lid}`). Resolution reuses the
   * existing `lookupKey` → store path (`:~` → `canonicalCells`), so a
   * cross-tenant source is fetched once and shared. First adopter of the
   * `DataRef` contract (see `data-address/adoption.md`).
   */
  readonly dataRef?: (props: AliasProps) => DataRef;
  /**
   * Invalidation path patterns this template's expansion DEPENDS on —
   * the row-node half of dependency routing. Declaring a pattern here
   * states "when an event matching this path fires, re-expanding this
   * template's instances is how the change lands row-scoped". The
   * declaration is LOAD-BEARING: `wireDeclaredReexpand` (applied by
   * `SheetModelCache` to every registered factory) subscribes the
   * sheet's invalidation sink and calls `reexpandAliasesByTemplate`
   * on a match — there is no second, hand-written subscription to
   * drift from. Patterns use the core `pathMatches` grammar (`*`
   * segment wildcards, parent subsumption), e.g.
   * `CalendarPreferencesChanged.pathFor("defaultWeatherLocation")`.
   *
   * Declare EXTERNAL dependencies only (preferences, visibility,
   * tasks, …). A pattern matching the sheet's own structural output
   * (`sheets.*.structure`, `rows.*`) would re-trigger on the events
   * the re-expansion itself emits.
   */
  readonly reexpandOn?: readonly string[];
  readonly dynamicTail?: DynamicTail;
  /**
   * Bidirectional bindings between child attributes and alias props
   * (rowkey README §9 "Bindings are bidirectional"). Each entry maps
   * the child-attribute path `child[<preset>].<attrName>` to the
   * alias prop name it round-trips with.
   *
   * Downward (alias prop → child attribute) runs at expansion time
   * via the existing prop-interpolation path in `expandChildSpecsRecursive`.
   *
   * Upward (child attribute write → alias prop) runs through the
   * package's own `propagateChildAttrToAlias` (not exported: a
   * persisting writer of the alias row; this repository's tests reach it
   * through `@tento-lona/sheets/internal-testing`), so the alias prop
   * catches up with a child attribute write (and persists via the alias
   * row's checked prop write).
   *
   * Example:
   *   `bindings: { "child[weather].lookupKey": "lookupKey" }`
   *   — when the weather data-source's `lookupKey` attribute is
   *   edited, the alias's `props.lookupKey` updates to match.
   */
  readonly bindings?: Readonly<Record<string, string>>;
}

import type { BuiltinAliasFactory } from "./builtin-alias-factories.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
/** Declared with its namespace in `builtin-alias-factories.ts`; re-exported for this module's importers. */
export type { BuiltinAliasFactory };

/**
 * Wire a factory's declared dependencies (`template.reexpandOn`) into
 * a live subscription on the sheet's invalidation sink. Applied by
 * `SheetModelCache` to every registered factory, so the declaration IS
 * the wiring — the exhaustiveness tests and the runtime consume the
 * same list. Factories with no declaration pass through untouched;
 * a custom `onAttach` still runs (both cleanups compose).
 */
export function wireDeclaredReexpand(
  factory: BuiltinAliasFactory,
): BuiltinAliasFactory {
  const patterns = factory.template.reexpandOn ?? [];
  if (patterns.length === 0) return factory;
  // Compiled once per factory: this subscriber sees EVERY event the sheet
  // emits (a timescale switch is tens of thousands in one frame), so the
  // per-event work must be a segment walk, not two string splits per
  // pattern.
  const matchers = patterns.map(compilePathPattern);
  return {
    ...factory,
    onAttach: (sheet) => {
      const unsubscribe = sheet.invalidationSink.subscribe((event) => {
        if (matchers.some((matches) => matches(event.path))) {
          // The fired path is the attribution the `alias/reexpand` breadcrumb
          // carries: the declared pattern is the fallback, this is the fact.
          sheetInternals(sheet).reexpandAliasesByTemplate(factory.templateName, {
            kind: "hidden-input",
            path: event.path,
          });
          // L4 (dark): the editable re-expansion re-declaration point — refresh
          // the reexpanded aliases' synthetic edges + their (possibly
          // newly-minted) child source rows' coverage. Reached type-only through
          // the store handle; MUST NOT value-import `ledger-synthetic` (TDZ, see
          // that module's header).
          sheetStores(sheet).sourceCellData.ledgerSynthetic?.onReexpand(
            sheet,
            factory.templateName,
          );
        }
      });
      const customCleanup = factory.onAttach?.(sheet);
      return () => {
        unsubscribe();
        customCleanup?.();
      };
    },
  };
}

// The process-wide alias-template fallback (always empty) is declared beside
// the registries that read it (`../sheet/sheet-registries.ts`), so that module
// needs this one for a type only; it is re-exported here under its old home.
export { ALIAS_TEMPLATES } from "../sheet/sheet-registries.ts";

/**
 * Look up an alias template, consulting the sheet's per-sheet
 * overlay first (populated from `BuiltinAliasFactory[]`) before
 * falling back to the sheet's `registries.aliasTemplates` (the built-ins
 * when there is no sheet).
 */
function lookupTemplate(
  name: string,
  sheet: Option<ModelSheet>,
): Option<AliasTemplate> {
  const overlay = sheet?.aliasTemplateOverlay(name) ?? null;
  if (overlay != null) return overlay;
  return (sheet?.registries ?? SheetRegistries.BUILTINS).aliasTemplates.get(name);
}

/**
 * Resolve an alias's declared **data address**. Given the alias's
 * template name + props, returns the `DataRef` the template declares, or
 * `null` if it declares none (or the template is unknown). This is the
 * read side of "querying `:~lona:weather` returns the data for
 * `:~weather:{lid}`": callers resolve the ref's `lookupKey` through the
 * normal store path (`:~` → `canonicalCells`, owner-scoped via `forRead`).
 */
function aliasDataRef(
  templateName: string,
  props: AliasProps,
  sheet: Option<ModelSheet>,
): Option<DataRef> {
  return lookupTemplate(templateName, sheet)?.dataRef?.(props) ?? null;
}

/**
 * Human-readable display name for an alias whose descriptor carries
 * `template`. Prefers the template's self-declared `displayName`,
 * falling back to a title-cased form of the template id ("alias."
 * prefix stripped, `-`/`_` spaced) for unregistered or unnamed
 * templates. Used as the alias display label so rows render a proper
 * name instead of the literal "alias" or a lowercase machine id.
 */
function aliasTemplateDisplayName(
  template: string,
  sheet: Option<ModelSheet>,
): string {
  const declared = lookupTemplate(template, sheet)?.displayName;
  if (declared !== undefined && declared.length > 0) return declared;
  const bare = template.replace(/^alias\./, "").replaceAll(/[-_]/g, " ");
  return bare.length === 0
    ? template
    : bare.charAt(0).toUpperCase() + bare.slice(1);
}

const EMPTY_ALIAS_LOCAL_STATE: AliasLocalState = Object.freeze({
  visibleByKey: new Map<string, boolean>(),
});

const WARNED_DYNAMIC_TAILS = new Set<string>();
const WARNED_UNKNOWN_ALIAS_TEMPLATES = new Set<string>();

function warnUnknownAliasTemplate(rowId: string, templateName: string): void {
  const key = `${rowId}|${templateName}`;
  if (WARNED_UNKNOWN_ALIAS_TEMPLATES.has(key)) return;
  WARNED_UNKNOWN_ALIAS_TEMPLATES.add(key);
  // `alias/unknown-template`: an alias row names a template no registry holds,
  // so its children will not hydrate. Once per (row, template).
  Tracing.log("alias/unknown-template", { rowId, template: templateName });
}

/** The signatures of the inputs/specs consumed by one completed expansion. */
export interface AliasExpansionSignatures {
  readonly inputSignature: string;
  readonly outputSignature: string;
}

/** Package-private prepared expansion; resolving a tail happens at most once. */
export interface AliasExpansionPlan extends AliasExpansionSignatures {
  materialize(): AliasExpansionSignatures;
}

function copyExpansionJson(value: Json): Json {
  if (Array.isArray(value)) return Object.freeze(value.map(copyExpansionJson));
  const object = Json.object(value);
  return object == null ? value : copyExpansionObject(object);
}

function copyExpansionObject(object: JsonObject): JsonObject {
  const copy: { [key: string]: Json } = {};
  for (const [key, value] of Object.entries(object)) copy[key] = copyExpansionJson(value);
  return Object.freeze(copy);
}

function copyExpansionSpecs(specs: readonly ChildSpec[]): readonly ChildSpec[] {
  return Object.freeze(specs.map((spec): ChildSpec => {
    const attributes: { [key: string]: Json | typeof PARENT_ROW_ID } = {};
    if (spec.attributes !== undefined) {
      for (const [key, value] of Object.entries(spec.attributes)) {
        attributes[key] = value === PARENT_ROW_ID ? value : copyExpansionJson(value);
      }
    }
    return Object.freeze({
      ...spec,
      ...(spec.attributes !== undefined ? { attributes: Object.freeze(attributes) } : {}),
      ...(spec.meta !== undefined ? { meta: copyExpansionObject(spec.meta) } : {}),
      ...(spec.children !== undefined ? { children: copyExpansionSpecs(spec.children) } : {}),
      ...(spec.graph !== undefined ? { graph: Object.freeze({ ...spec.graph }) } : {}),
    });
  }));
}

/**
 * Capture static inputs now, and share resolved specs between signatures and
 * materialization. An initial source-backed tail resolves after the static
 * children exist, matching the original order. Re-expansion reads signatures
 * before dropping; that resolves its tail once at admission.
 */
function prepareAliasExpansion(
  alias: AliasRow,
  sheet: ModelSheet,
  localState: AliasLocalState = aliasLocalState(alias),
  /**
   * Publish the template's data address on the alias row (the expansion's
   * one write before it materializes). `false` for a query: only its
   * signatures are read (`SheetAliasReexpansion.inputMoved`).
   */
  publish = true,
): Option<AliasExpansionPlan> {
  if (alias.instance().hydrated) return null;
  const templateName = alias.descriptor.template;
  if (templateName.length === 0) return null;
  const template = lookupTemplate(templateName, sheet);
  if (template == null) return null;
  const props = copyExpansionObject(alias.instance().props ?? {});
  // The one store this plan reads and patches: the alias row's metadata.
  const rowMetadata = sheetStores(sheet).rowMetadata;
  // Publish the data address before deriving actual static children, as the
  // original expander did. The encompassing operation is already guarded.
  if (publish && template.dataRef !== undefined) {
    const ref = template.dataRef(props);
    const meta = rowMetadata.get(alias.id);
    if (ref.lookupKey.length > 0 && meta.lookupKey !== ref.lookupKey) {
      rowMetadata.patch(alias.id, { lookupKey: ref.lookupKey });
    }
  }
  const anchor = aliasHydrationAnchor(alias);
  if (anchor == null) return null;

  const attributes = copyExpansionObject(rowMetadata.get(alias.id).attributes ?? {});
  const timescale = sheet.timescale;
  const context: AliasTemplateContext = { timescale, registries: sheet.registries, attributes };
  const rawChildren = typeof template.rule.children === "function"
    ? template.rule.children(props, context)
    : template.rule.children;
  const children = copyExpansionSpecs(anchor.presetPrefix == null
    ? rawChildren
    : prefixChildSpecPresets(anchor.presetPrefix, rawChildren));
  const staticSignature = JSON.stringify({ p: props, t: timescale, a: attributes });
  const dynamicTail = template.dynamicTail ?? null;
  const tailParentPreset = dynamicTail?.parentPreset ?? null;
  const visibility: AliasLocalState = { visibleByKey: new Map(localState.visibleByKey) };
  let resolvedTail: Option<readonly ChildSpec[]> = null;
  const tail = (): Option<readonly ChildSpec[]> => {
    if (dynamicTail == null) return null;
    if (resolvedTail == null) {
      const items = resolveDynamicTailItems(dynamicTail, props, sheet);
      resolvedTail = copyExpansionSpecs(items.flatMap((item, index) =>
        dynamicTail.filter && !dynamicTail.filter(item, visibility, index)
          ? []
          : [dynamicTail.as(item, props, index)]));
    }
    return resolvedTail;
  };
  let inputSignature: Option<string> = null;
  let outputSignature: Option<string> = null;
  const plan: AliasExpansionPlan = {
    get inputSignature(): string {
      if (inputSignature == null) {
        const specs = tail();
        inputSignature = '{"s":' + staticSignature + ',"tail":' +
          JSON.stringify(specs == null ? null : tailSpecSignature(specs)) + '}';
      }
      return inputSignature;
    },
    get outputSignature(): string {
      if (outputSignature == null) {
        const specs = tail();
        outputSignature = JSON.stringify({ specs: children, tail: specs == null ? null : tailSpecSignature(specs) },
          (_key, value) => value instanceof Rational ? value.toString() : value);
      }
      return outputSignature;
    },
    materialize(): AliasExpansionSignatures {
      expandChildSpecsRecursive(sheet, alias.id, anchor.parentHex, children, null);
      const specs = tail();
      if (dynamicTail != null && specs != null) {
        expandDynamicTail(templateName, tailParentPreset, specs,
          sheet, anchor.parentHex, anchor.presetPrefix);
      }
      return Object.freeze({
        inputSignature: plan.inputSignature,
        outputSignature: plan.outputSignature,
      });
    },
  };
  return Object.freeze(plan);
}

function expandAliasInstance(
  alias: AliasRow,
  sheet: ModelSheet,
  localState: Option<AliasLocalState> = null,
): void {
  sheetInternals(sheet).withAliasExpansion(alias, (materialize) => {
    if (alias.instance().hydrated) {
      // Authored/forked children remain additive; they certify no template plan.
      reconcileHydratedAliasChildren(alias, sheet);
      return;
    }
    const templateName = alias.descriptor.template;
    if (templateName.length === 0) {
      throw new Error(`expandAliasInstance: alias row ${alias.id.rawId} has an empty template.`);
    }
    const plan = prepareAliasExpansion(alias, sheet, localState ?? aliasLocalState(alias));
    if (plan != null) {
      materialize(plan);
      return;
    }
    if (lookupTemplate(templateName, sheet) == null) {
      warnUnknownAliasTemplate(alias.id.rawId, templateName);
      return;
    }
    throw new Error(
      `expandAliasInstance: alias row ${alias.id.rawId} cannot hydrate ` +
        `template "${templateName}" because it has no UUID-backed anchor.`,
    );
  });
}

/**
 * Derive the factory child specs for `alias` from its `template` rule,
 * prefixing presets when the alias is itself a synthetic (virtual)
 * child. Shared by full expansion (attached aliases) and hydration
 * reconcile (already-hydrated aliases) so both see the same spec list.
 */
function deriveAliasChildSpecs(
  alias: AliasRow,
  sheet: ModelSheet,
  template: AliasTemplate,
  anchor: { readonly parentHex: string; readonly presetPrefix: Option<string> },
): readonly ChildSpec[] {
  const ctx: AliasTemplateContext = {
    timescale: sheet.timescale,
    registries: sheet.registries,
    attributes: sheetStores(sheet).rowMetadata.get(alias.id).attributes ?? {},
  };
  const props = alias.instance().props as AliasProps;
  const rawChildren = typeof template.rule.children === "function"
    ? template.rule.children(props, ctx)
    : template.rule.children;
  return anchor.presetPrefix == null
    ? rawChildren
    : prefixChildSpecPresets(anchor.presetPrefix, rawChildren);
}

/**
 * Hydration reconcile for an already-hydrated alias. Its children are
 * real, server-backed rows (authored or forked) rather than template
 * synthetics, so structural children the factory ADDS after the instance
 * was materialized never appear. Walk the factory's current child specs
 * and additively synthesize any marked `reconcile: true` that the
 * materialized subtree is missing — leaving every authored/user child
 * untouched. A structural no-op when the template, anchor, or a parent
 * along the path to a reconcile spec is absent.
 */
function reconcileHydratedAliasChildren(alias: AliasRow, sheet: ModelSheet): void {
  const templateName = alias.descriptor.template;
  if (templateName.length === 0) return;
  const template = lookupTemplate(templateName, sheet);
  if (template == null) return;
  const anchor = aliasHydrationAnchor(alias);
  if (anchor == null) return;

  const children = deriveAliasChildSpecs(alias, sheet, template, anchor);
  reconcileChildSpecsAdditive(sheet, alias.id, anchor, children);
}

/**
 * The hydration-reconcile anchor for a materialized row: where a child
 * synthesized under it must be keyed. A real (`r_…`) row anchors its own
 * hex with no preset prefix; a synthetic (virtual) row anchors the real
 * ancestor hex and carries the accumulated preset path, so a child nests
 * as `virtual(r_<ancestorHex>|<preset>/<child>)` — mirroring how
 * `expandChildSpecsRecursive` nests under a virtual parent. Generalizes
 * `aliasHydrationAnchor` to any `ModelRow`.
 */
function rowReconcileAnchor(
  row: ModelRow,
): Option<{ readonly parentHex: string; readonly presetPrefix: Option<string> }> {
  const hex = row.id.key.toHex();
  if (hex != null) return { parentHex: hex, presetPrefix: null };
  const virtual = row.id.key.asVirtual();
  if (virtual != null) {
    return { parentHex: virtual.parentHex, presetPrefix: virtual.preset };
  }
  return null;
}

/**
 * Reconcile factory child `specs` into an already-hydrated alias's
 * materialized subtree under `parentId` (anchored by `anchor`). For each
 * spec: if a matching real child exists, descend to reconcile its nested
 * specs; if none exists and the spec is marked `reconcile: true`,
 * synthesize it (and its subtree) in place. Non-reconcile specs with no
 * match are left alone — the authored/forked tree owns its own structure.
 *
 * The synthesized spec's preset is prefixed by `anchor.presetPrefix` when
 * the parent is itself a synthetic (virtual) row, so the new id nests
 * under the same real ancestor hex (the live calendar body is the
 * virtual `…|timeline` row, so the sticker subcolumn lands at
 * `…|timeline/stickers`). Synthesis routes through
 * `expandChildSpecsRecursive` → `Sheet.addRow`, which expands any alias
 * it creates (so a reconciled `alias.stickers` materializes its inner
 * source) and is idempotent via the virtual-id guard.
 */
function reconcileChildSpecsAdditive(
  sheet: ModelSheet,
  parentId: ModelRow["id"],
  anchor: { readonly parentHex: string; readonly presetPrefix: Option<string> },
  specs: readonly ChildSpec[],
): void {
  const parent = sheet.peekRow(parentId);
  if (parent == null) return;
  const existing = parent.children;

  for (const spec of specs) {
    if (spec.placement !== "child") continue;
    const match = matchExistingChild(existing, spec);

    if (match == null) {
      if (spec.reconcile !== true) continue;
      const childSpecs = anchor.presetPrefix == null
        ? [spec]
        : prefixChildSpecPresets(anchor.presetPrefix, [spec]);
      expandChildSpecsRecursive(
        sheet,
        parentId,
        anchor.parentHex,
        childSpecs,
        null,
      );
      continue;
    }

    const nested = spec.children ?? [];
    if (nested.length === 0) continue;
    const childAnchor = rowReconcileAnchor(match);
    if (childAnchor == null) continue;
    reconcileChildSpecsAdditive(sheet, match.id, childAnchor, nested);
  }
}

/**
 * Find the materialized child of an already-hydrated alias that
 * corresponds to factory child `spec`, so reconcile can descend into it
 * rather than re-synthesizing. Hydrated children are real rows that
 * dropped their synthetic preset on materialization, so match on stable
 * structural identity: alias children by template, layout children by
 * layout type. Other roles are never reconcile-traversal parents.
 */
function matchExistingChild(
  existing: readonly ModelRow[],
  spec: ChildSpec,
): Option<ModelRow> {
  for (const row of existing) {
    const d = row.descriptor;
    if (d.role === "alias") {
      const wantTemplate = spec.attributes?.template;
      if (typeof wantTemplate === "string" && d.template === wantTemplate) {
        return row;
      }
    } else if (d.role === "layout") {
      if (d.layout === spec.type) return row;
      // Calendar bodies were persisted as timeline/grid before the stable
      // calendar-body layout. Keep additive children reachable on those rows.
      if (spec.type === "calendar-body" &&
          (d.layout === "timeline[column]" || d.layout === "grid")) return row;
    }
  }
  return null;
}

function aliasHydrationAnchor(alias: AliasRow): Option<{
  readonly parentHex: string;
  readonly presetPrefix: Option<string>;
}> {
  const parentHex = alias.id.key.toHex();
  if (parentHex != null) return { parentHex, presetPrefix: null };

  const virtual = alias.id.key.asVirtual();
  if (virtual != null) {
    return { parentHex: virtual.parentHex, presetPrefix: virtual.preset };
  }

  return null;
}

function aliasLocalState(alias: AliasRow): AliasLocalState {
  const visibleByKey = new Map<string, boolean>();
  const overrides = alias.instance().overrides;
  const visibleOverrides = overrides.visibleByKey;

  if (visibleOverrides instanceof Map) {
    for (const [key, value] of visibleOverrides) {
      if (typeof key === "string" && typeof value === "boolean") {
        visibleByKey.set(key, value);
      }
    }
  } else if (isRecord(visibleOverrides)) {
    for (const [key, value] of Object.entries(visibleOverrides)) {
      if (typeof value === "boolean") visibleByKey.set(key, value);
    }
  }

  for (const [key, value] of Object.entries(overrides)) {
    if (typeof value !== "boolean") continue;
    if (key.startsWith("visibleByKey.")) {
      visibleByKey.set(key.slice("visibleByKey.".length), value);
    } else if (key.startsWith("visible.")) {
      visibleByKey.set(key.slice("visible.".length), value);
    }
  }

  return { visibleByKey };
}

function resolveDynamicTailSourceKey(alias: AliasRow): Option<string> {
  const templateName = alias.descriptor.template;
  if (templateName.length === 0) {
    throw new Error(
      `resolveDynamicTailSourceKey: alias row ${alias.id.rawId} has an empty template.`,
    );
  }
  const template = lookupTemplate(templateName, sheetOf(alias));
  if (template?.dynamicTail === undefined) return null;
  if (template.dynamicTail.source === undefined) return null;
  return interpolateProps(
    template.dynamicTail.source,
    alias.instance().props as AliasProps,
  );
}

/**
 * Upward leg of `AliasTemplate.bindings`: when a plugin writes an
 * attribute on an alias-hydrated child, call this helper so the
 * matching alias prop catches up and persists.
 *
 * Walks the tree from `child` upward looking for the nearest
 * ancestor `AliasRow`, resolves the child's preset name within
 * that alias's subtree, and — if the
 * `child[<preset>].<attrName>` key is in the binding map — calls
 * `aliasWriterOf(alias).setProp(boundProp, value)`. The write flows through
 * the in-memory store, mirrors into `attributes.props`, and
 * writes back to the server.
 *
 * Returns `true` when a binding was applied; `false` otherwise so
 * callers can distinguish "edit landed in the binding map" from
 * "edit fell through as a plain instance override / detach
 * trigger" (rowkey README §9 lifecycle).
 */
export async function propagateChildAttrToAlias(
  child: ModelRow,
  attrName: string,
  value: Json,
): Promise<boolean> {
  const ancestor = nearestAliasAncestor(child);
  if (ancestor == null) return false;
  const { alias, childPreset } = ancestor;
  const template = lookupTemplate(alias.descriptor.template, sheetOf(alias));
  const bindings = template?.bindings;
  if (bindings === undefined) return false;
  const bindingKey = `child[${childPreset}].${attrName}`;
  const propName = bindings[bindingKey];
  if (propName === undefined) return false;
  await aliasWriterOf(alias).setProp(propName, value);
  return true;
}

function nearestAliasAncestor(
  child: ModelRow,
): Option<{ alias: AliasRow; childPreset: string }> {
  let cursor: Option<ModelRow> = child;
  let presetTrail: string[] = [];
  while (cursor != null) {
    const presetSegment = cursor.id.key.asVirtual()?.preset;
    if (presetSegment != null) {
      presetTrail = presetTrail.length === 0
        ? [presetSegment]
        : [presetSegment, ...presetTrail];
    }
    const parent: Option<ModelRow> = cursor.parent ?? null;
    if (parent != null && parent instanceof AliasRow) {
      const childPreset = presetTrail.join("/");
      return childPreset.length > 0
        ? { alias: parent, childPreset }
        : null;
    }
    cursor = parent;
  }
  return null;
}

function resolveInitialAliasProps(
  templateName: string,
  attributes: Option<JsonObject>,
  rawRow: Option<RawRowInput>,
): JsonObject;
function resolveInitialAliasProps(
  templateName: string,
  attributes: Option<JsonObject>,
  rawRow: Option<RawRowInput>,
  sheet: Option<ModelSheet>,
): JsonObject;
function resolveInitialAliasProps(
  templateName: string,
  attributes: Option<JsonObject>,
  rawRow: Option<RawRowInput>,
  sheet: Option<ModelSheet> = null,
): JsonObject {
  const inline = Json.child(attributes, "props");
  const base: { [key: string]: Json } = inline != null ? { ...inline } : {};

  applyLegacyAliasProps(templateName, base, rawRow);

  const template = lookupTemplate(templateName, sheet);
  if (template?.props === undefined || attributes == null) {
    return base;
  }

  for (const key of Object.keys(template.props)) {
    if (key in base) continue;
    const value = attributes[key];
    if (value !== undefined) base[key] = value;
  }
  return base;
}

/**
 * Add `specs` as virtual children of `parentId` (recursively), resolving
 * each spec's attributes against the parent's (`PARENT_ROW_ID`, `{key}`
 * interpolation) and honouring the parent's `children` visibility map.
 * A child whose own type declares child seeds (or whose attributes carry
 * JSON ones) expands those under it.
 */
function expandChildSpecsRecursive(
  sheet: ModelSheet,
  parentId: ModelRow["id"],
  parentHex: string,
  specs: readonly ChildSpec[],
  parentAttrs: Option<JsonObject>,
): void {
  if (specs.length === 0) return;

  const childrenConfig = Json.child(parentAttrs, "children");
  const filtered = specs.filter((spec) => {
    if (isRendererWireType(spec.type)) return true;
    return Json.boolean(Json.child(childrenConfig, spec.preset), "visible") ?? true;
  });

  for (const spec of filtered) {
    if (spec.placement !== "child") continue;

    const virtualId = `virtual(r_${parentHex}|${spec.preset})`;
    // Idempotent: a row type that is BOTH an alias (expanded via its template
    // in the hydrate alias-loop) AND carries `childSpecs` (expanded in the
    // childSpec-loop) — e.g. the legacy `calendar` descriptor — would expand
    // the same virtual children twice. Skip any child already materialized
    // rather than letting `addRow` throw "already exists".
    if (sheet.peekRow(sheet.registry.resolveFromRawId(virtualId)) != null) continue;
    const specAttrs: { [key: string]: Json } = spec.attributes
      ? resolveChildSpecAttributes(spec.attributes, parentHex, parentAttrs)
      : {};
    if (spec.storage === "parent") {
      specAttrs.parentRowId = parentId.rawId;
    }

    const child = sheet.addRow({
      id: RowNetworkKey.fromWire(virtualId),
      type: spec.type,
      parentId: RowNetworkKey.fromWire(parentId.rawId),
      ...(spec.label !== undefined ? { label: spec.label } : {}),
      ...(spec.lookupKey !== undefined
        ? { lookupKey: interpolateParentAttrs(spec.lookupKey, parentAttrs) }
        : {}),
      ...(spec.formula !== undefined ? { formula: spec.formula } : {}),
      ...(spec.heightPx !== undefined ? { heightPx: spec.heightPx } : {}),
      ...(spec.meta !== undefined ? { meta: spec.meta } : {}),
      ...(Object.keys(specAttrs).length > 0 ? { attributes: specAttrs } : {}),
      ...(spec.graph !== undefined ? { graph: spec.graph } : {}),
    });

    const childHex = RowKey.of(virtualId).toHex();
    const childAttrs = sheetStores(sheet).rowMetadata.get(child.id).attributes ?? null;
    const rawNestedChildSpecs = [
      ...(spec.children ?? []),
      ...RowTypeSpecOps.childSpecsFor(spec.type, {
        id: virtualId,
        type: spec.type,
        ...(childAttrs != null ? { attributes: childAttrs } : {}),
      }, sheet.registries),
    ];
    if (rawNestedChildSpecs.length === 0) continue;

    const nestedParentHex = childHex ?? parentHex;
    const nestedChildSpecs = childHex == null
      ? prefixChildSpecPresets(spec.preset, rawNestedChildSpecs)
      : rawNestedChildSpecs;
    if (nestedChildSpecs.length === 0) continue;

    expandChildSpecsRecursive(
      sheet,
      child.id,
      nestedParentHex,
      nestedChildSpecs,
      childAttrs,
    );
  }
}

function prefixChildSpecPresets(
  prefix: string,
  specs: readonly ChildSpec[],
): ChildSpec[] {
  const prefixed: ChildSpec[] = [];
  for (const spec of specs) {
    prefixed.push({
      ...spec,
      preset: `${prefix}/${spec.preset}`,
      ...(spec.children !== undefined
        ? { children: prefixChildSpecPresets(prefix, spec.children) }
        : {}),
    });
  }
  return prefixed;
}

/**
 * A child spec's attribute bag as the child row carries it: every
 * `PARENT_ROW_ID` becomes the parent's `r_<hex>` and every `{key}` in a
 * string is interpolated from the parent's attributes.
 */
function resolveChildSpecAttributes(
  attributes: ChildSpecAttributes,
  parentHex: string,
  parentAttrs: Option<JsonObject>,
): { [key: string]: Json } {
  const out: { [key: string]: Json } = {};
  for (const [key, value] of Object.entries(attributes)) {
    out[key] = value === PARENT_ROW_ID
      ? `r_${parentHex}`
      : resolveChildSpecValue(value, parentAttrs);
  }
  return out;
}

function resolveChildSpecValue(
  value: Json,
  parentAttrs: Option<JsonObject>,
): Json {
  if (typeof value === "string") {
    return interpolateParentAttrs(value, parentAttrs);
  }
  if (Array.isArray(value)) {
    return value.map((item: Json) => resolveChildSpecValue(item, parentAttrs));
  }
  const obj = Json.object(value);
  if (obj != null) {
    const out: { [key: string]: Json } = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = resolveChildSpecValue(v, parentAttrs);
    }
    return out;
  }
  return value;
}

function interpolateParentAttrs(
  template: string,
  attrs: Option<JsonObject>,
): string {
  return template.replaceAll(/\{([^}]+)\}/g, (_match, key: string) => {
    const value = attrs?.[key];
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }
    throw new Error(
      `interpolateParentAttrs: missing required attr "${key}" for template ` +
        `"${template}".`,
    );
  });
}

function isSyntheticAliasChild(row: ModelRow): boolean {
  let parent = row.parent;
  while (parent != null) {
    if (parent instanceof AliasRow) {
      return !parent.instance().hydrated;
    }
    parent = parent.parent;
  }
  return false;
}

/**
 * The nearest alias ancestor of `row` IF it is still attached
 * (template-driven) — i.e. the alias whose synthetic child `row` is.
 * Returns `null` when there is no alias ancestor, or when the nearest
 * one is already detached (so `row` is a real, editable forked-group
 * member). The fork target for a structural edit on `row`.
 */
function owningAttachedAlias(row: ModelRow): Option<AliasRow> {
  let parent = row.parent;
  while (parent != null) {
    if (parent instanceof AliasRow) {
      return parent.instance().hydrated ? null : parent;
    }
    parent = parent.parent;
  }
  return null;
}

function warnDynamicTailNotImplemented(
  templateName: string,
  _localState: AliasLocalState,
): void {
  if (WARNED_DYNAMIC_TAILS.has(templateName)) return;
  WARNED_DYNAMIC_TAILS.add(templateName);
  // `alias/dynamic-tail-unimplemented`: a template declares a `dynamicTail`
  // this expander cannot run yet. Once per template.
  Tracing.log("alias/dynamic-tail-unimplemented", { template: templateName });
}

function expandDynamicTail(
  templateName: string,
  declaredParentPreset: Option<string>,
  rawChildren: readonly ChildSpec[],
  sheet: ModelSheet,
  aliasParentHex: string,
  presetPrefix: Option<string>,
): void {
  // When `parentPreset` is set, attach under the named static child.
  // When omitted, attach directly under the alias root (R6.2's
  // client-hydrated alias pattern — the alias has no static children
  // worth keeping as a placeholder).
  const parentPreset = declaredParentPreset === null
    ? null
    : (presetPrefix === null
      ? declaredParentPreset
      : `${presetPrefix}/${declaredParentPreset}`);
  const parentLookupId = parentPreset === null
    ? (presetPrefix === null
      ? `r_${aliasParentHex}`
      : `virtual(r_${aliasParentHex}|${presetPrefix})`)
    : `virtual(r_${aliasParentHex}|${parentPreset})`;
  const parent = sheet.peekRow(sheet.registry.resolveFromRawId(parentLookupId));
  if (parent == null) {
    throw new Error(
      `expandDynamicTail: alias template "${templateName}" expected ` +
        `parent "${parentLookupId}" to exist.`,
    );
  }

  if (rawChildren.length === 0) return;

  const parentHex = parent.id.key.toHex();
  // When the parent is itself virtual, prefix nested presets so their
  // synthesized ids stay deterministic. Use the parent's own preset
  // (parentPreset when set, else the alias's own presetPrefix).
  const prefix = parentPreset ?? presetPrefix;
  const children = parentHex == null && prefix != null
    ? prefixChildSpecPresets(prefix, rawChildren)
    : rawChildren;
  const parentAttrs = sheetStores(sheet).rowMetadata.get(parent.id).attributes ?? null;
  expandChildSpecsRecursive(
    sheet,
    parent.id,
    parentHex ?? aliasParentHex,
    children,
    parentAttrs,
  );
}

/**
 * Deterministic signature of everything `expandAliasInstance` derives the
 * alias's children FROM: the static inputs (props, timescale, attributes —
 * exactly what `deriveAliasChildSpecs` reads) plus, for dynamic-tail
 * templates, the RESOLVED tail projection (each surviving item's ChildSpec
 * identity fields). Equal signatures ⇒ re-expansion would rebuild an
 * identical subtree, so `Sheet.reexpandAttachedAlias` skips its
 * drop+rebuild — the guard that stops a data settle from churning the row
 * set ("a TIME-axis data load must NOT mutate the ROW set").
 *
 * Returns `null` (no signature — always reconcile) when the alias is
 * hydrated or its template is unregistered, preserving those arms exactly.
 *
 * Templates that read an input the signature can't see (e.g.
 * `alias.default-weather` deriving its child key from
 * `prefs.live.defaultWeatherLocation`) must keep fanning out through
 * `reexpandAliasesByTemplate` — its `force` flag bypasses the skip.
 */
/**
 * The OUTPUT signature of an attached alias: its derived child specs (the
 * exact list `expandAliasInstance` would materialize now — template rule,
 * props, timescale, attributes, preset prefix all applied), serialized
 * deterministically. Compare two of these and you know whether a
 * re-expansion would build a different subtree, without building it.
 *
 * This is the sibling of `aliasExpansionSignature`, which hashes the
 * INPUTS. The inputs change on every timescale switch by definition
 * (`t: sheet.timescale`), so an input signature can never say "unchanged"
 * for the one case that matters most: a template whose children do not
 * read the timescale (the calendar's are declared timescale-stable except
 * for the month branch). `Sheet.reexpandAttachedAlias` in `"output"` mode
 * drops a subtree only when THIS differs from what it last built — the
 * switch-reinstall plan's rule.
 *
 * A `dynamicTail` is part of the output too: its items come from a source
 * row, not from the timescale, so the RESOLVED tail (the same projection
 * `aliasExpansionSignature` uses) rides in the signature and an unchanged
 * tail is no reason to drop. Returns `null` (no signature — always rebuild)
 * when the alias is hydrated, its template is unregistered, or it has no
 * hydration anchor.
 */
function aliasChildSpecSignature(alias: AliasRow, sheet: ModelSheet): Option<string> {
  if (alias.instance().hydrated) return null;
  const templateName = alias.descriptor.template;
  if (templateName.length === 0) return null;
  const template = lookupTemplate(templateName, sheet);
  if (template == null) return null;
  const anchor = aliasHydrationAnchor(alias);
  if (anchor == null) return null;
  const specs = deriveAliasChildSpecs(alias, sheet, template, anchor);
  const dynamicTail = template.dynamicTail;
  const tail = dynamicTail === undefined
    ? null
    : resolvedTailSignature(dynamicTail, alias, sheet, aliasLocalState(alias));
  return JSON.stringify({ specs, tail }, (_key, value) =>
    value instanceof Rational ? value.toString() : value,
  );
}

/** The resolved dynamic-tail specs as a serializable projection — shared by the
 * input signature (`aliasExpansionSignature`) and the output one. */
function tailSpecSignature(specs: readonly ChildSpec[]) {
  return specs.map((spec) => ({
    preset: spec.preset,
    type: spec.type,
    lookupKey: spec.lookupKey ?? null,
    label: spec.label ?? null,
    meta: spec.meta ?? null,
    attributes: spec.attributes ?? null,
    pos: Rational.toString(spec.position),
  }));
}

function resolvedTailSignature(
  dynamicTail: DynamicTail,
  alias: AliasRow,
  sheet: ModelSheet,
  localState: AliasLocalState,
) {
  const props = alias.instance().props ?? {};
  const items = resolveDynamicTailItems(dynamicTail, props, sheet);
  return tailSpecSignature(items.flatMap((item, index) =>
    dynamicTail.filter && !dynamicTail.filter(item, localState, index)
      ? []
      : [dynamicTail.as(item, props, index)]));
}

function aliasExpansionSignature(
  alias: AliasRow,
  sheet: ModelSheet,
  localState: AliasLocalState,
): Option<string> {
  if (alias.instance().hydrated) return null;
  const templateName = alias.descriptor.template;
  if (templateName.length === 0) return null;
  const template = lookupTemplate(templateName, sheet);
  if (template == null) return null;

  const props = (alias.instance().props ?? {}) as AliasProps;
  const staticPart = {
    p: props,
    t: sheet.timescale,
    a: sheetStores(sheet).rowMetadata.get(alias.id).attributes ?? {},
  };
  const dynamicTail = template.dynamicTail;
  if (dynamicTail === undefined) {
    return JSON.stringify({ s: staticPart, tail: null });
  }
  const tail = resolvedTailSignature(dynamicTail, alias, sheet, localState);
  return JSON.stringify({ s: staticPart, tail });
}

function resolveDynamicTailItems(
  dynamicTail: DynamicTail,
  props: AliasProps,
  sheet: ModelSheet,
): CellValue[] {
  if (dynamicTail.items !== undefined) {
    return [...dynamicTail.items(props, sheet)];
  }

  if (dynamicTail.source === undefined) {
    throw new Error(
      `resolveDynamicTailItems: dynamicTail must declare source or items.`,
    );
  }

  const sourceKey = interpolateProps(dynamicTail.source, props);
  const source = sheet.peekByLookupKey(sourceKey);
  if (!(source instanceof SourceRow)) {
    // The backing source can legitimately be absent (e.g. a task-list alias
    // hydrated before its lists source exists, or a minimal sheet). Expand to
    // zero dynamic-tail items rather than aborting hydrate — but surface it so
    // a genuinely misconfigured source isn't silently hidden.
    // `alias/dynamic-tail-source-missing`: the tail's lookup key resolved to
    // no source row, so the tail expands to no items.
    Tracing.log("alias/dynamic-tail-source-missing", { lookupKey: sourceKey });
    return [];
  }

  const meta = sheetStores(sheet).rowMetadata.get(source.id).meta;
  const metaItems = itemsFromMeta(meta ?? null);
  if (metaItems.length > 0) return metaItems;

  const out: CellValue[] = [];
  for (const cell of SourceCells.dates(source.peekCells(null))) {
    const value = cell.value;
    if (Array.isArray(value)) {
      for (const item of value) out.push(item);
      continue;
    }
    if (value != null) out.push(value);
  }
  return out;
}

function itemsFromMeta(
  meta: Option<JsonObject>,
): CellValue[] {
  if (!meta) return [];
  const direct = Json.array(meta, "items");
  if (direct != null) return [...direct];

  for (const value of Object.values(meta)) {
    if (Array.isArray(value)) return [...value];
  }
  return [];
}

function interpolateProps(template: string, props: AliasProps): string {
  return template.replaceAll(/\{([^}]+)\}/g, (_match, name: string) => {
    const value = props[name];
    if (value != null) return String(value);
    throw new Error(
      `interpolateProps: missing required prop "${name}" for template ` +
        `"${template}".`,
    );
  });
}

function applyLegacyAliasProps(
  templateName: string,
  base: Record<string, unknown>,
  rawRow: Option<RawRowInput>,
): void {
  if (rawRow == null) return;
  if (templateName === "data" && base.lookupKey === undefined) {
    const lookupKey = legacyLookupKey(rawRow);
    if (lookupKey != null) base.lookupKey = lookupKey;
    return;
  }
  if (
    (templateName === "alias.weather" ||
      templateName === "alias.default-weather") &&
    base.locationId === undefined
  ) {
    // `alias.default-weather` is preference-driven: its child derives
    // `:~weather:{lid}` from `prefs.live.defaultWeatherLocation` at expansion time
    // (`preferences-aliases.ts`), reading `props.locationId ?? prefs`. Stamping
    // a `locationId` here — the canonical `:~lona:weather` key parses to the
    // DEFAULT lid — would pin the prop and permanently mask the preference, so
    // changing the default location would never re-key the child. Only the
    // per-row `alias.weather` template stores an explicit `locationId`; the
    // default template stays prop-less and tracks the preference.
    //
    // This lifting stamps ONLY `locationId` (data-address migration
    // Phase B): the KEY itself is produced solely by the template's
    // `dataRef` (`weatherLookupKey` — explicit, owner-scoped). An
    // inline `props.lookupKey` is left as-authored; `weatherLookupKey`
    // normalizes any legacy form through its parsed location id.
    const stampLocationId = templateName === "alias.weather";
    const inlineLookupKey = typeof base.lookupKey === "string"
      ? base.lookupKey
      : null;
    if (inlineLookupKey !== null) {
      const inlineLocationId = parseWeatherLocationId(inlineLookupKey);
      if (inlineLocationId !== null) {
        if (stampLocationId) base.locationId = inlineLocationId;
        return;
      }
    }

    const lookupKey = legacyLookupKey(rawRow);
    const locationId = lookupKey ? parseWeatherLocationId(lookupKey) : null;
    if (locationId !== null) {
      if (stampLocationId) base.locationId = locationId;
      return;
    }
    const legacyLocationId = legacyWeatherLocationId(rawRow);
    if (legacyLocationId !== null && stampLocationId) {
      base.locationId = legacyLocationId;
    }
    return;
  }
  if (templateName === "alias.timezone" && base.tz === undefined) {
    const tz = rawRow.attributes?.tz ?? rawRow.attributes?.timezone;
    if (typeof tz === "string") base.tz = tz;
  }
  // Calendar's `secondaryTimezones` is per-user UI preference data,
  // not wire-row attributes — there's nothing to lift here. The
  // `alias.secondary-timezones` template is supplied as a
  // `BuiltinAliasFactory` from the layer above (R6.2).
}

function legacyLookupKey(rawRow: RawRowInput): Option<string> {
  if (rawRow.lookupKey !== undefined) return rawRow.lookupKey;
  const attrLookupKey = rawRow.attributes?.lookupKey;
  return typeof attrLookupKey === "string" ? attrLookupKey : null;
}

function parseWeatherLocationId(lookupKey: string): Option<string> {
  // Strip the optional `[user@host]:` user-scoped prefix — applies to
  // cross-user / multi-tenant lookup keys; the location id sits after
  // the unscoped portion.
  const unscoped = lookupKey.startsWith("[")
    ? (() => {
      const close = lookupKey.indexOf("]:");
      return close === -1 ? lookupKey : lookupKey.slice(close + 1);
    })()
    : lookupKey;
  // Bare `:~weather` / `:weather` keys are deprecated — no stored row
  // carries them and the server rejects them. The `:~lona:weather` alias
  // identity names no location: nothing is lifted from it (operator
  // ruling: nothing invented).
  const prefix = ":~weather:";
  if (!unscoped.startsWith(prefix)) return null;
  const value = unscoped.slice(prefix.length);
  return value.length > 0 ? value : null;
}

function legacyWeatherLocationId(rawRow: RawRowInput): Option<string> {
  const attrs = rawRow.attributes;
  if (attrs === undefined) return null;

  const locationId = attrs.locationId;
  if (typeof locationId === "string" && locationId.length > 0) {
    return locationId;
  }

  const lid = attrs.lid;
  if (typeof lid === "string" && lid.length > 0) return lid;
  if (typeof lid === "number" && Number.isFinite(lid)) return String(lid);
  return null;
}

function isRecord(
  value: unknown,
): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// ── AliasTemplate namespace ──────────────────────────────────────

const $expandAliasInstance = expandAliasInstance;
const $prepareAliasExpansion = prepareAliasExpansion;
const $aliasExpansionSignature = aliasExpansionSignature;
const $aliasChildSpecSignature = aliasChildSpecSignature;
const $aliasLocalState = aliasLocalState;
const $resolveDynamicTailSourceKey = resolveDynamicTailSourceKey;
const $resolveInitialAliasProps = resolveInitialAliasProps;
const $expandChildSpecsRecursive = expandChildSpecsRecursive;
const $isSyntheticAliasChild = isSyntheticAliasChild;
const $owningAttachedAlias = owningAttachedAlias;
const $aliasTemplateDisplayName = aliasTemplateDisplayName;
const $aliasDataRef = aliasDataRef;

/**
 * Whether `alias`'s template declares child rows: static children for its
 * props, or a dynamic tail (whose items may not have materialized yet). A
 * template that declares none is legitimately childless; one that declares
 * some but expanded to none has not materialized them (`SheetAliasFork.detach`).
 */
function aliasTemplateDeclaresChildren(alias: AliasRow, sheet: ModelSheet): boolean {
  const template = lookupTemplate(alias.descriptor.template, sheet);
  if (template == null) return false;
  if (template.dynamicTail != null) return true;
  const children = template.rule.children;
  if (typeof children !== "function") return children.length > 0;
  const attributes = copyExpansionObject(alias.sheetReads.rowMeta(alias.id).attributes ?? {});
  const props = copyExpansionObject(alias.instance().props ?? {});
  return children(props, { timescale: sheet.timescale, registries: sheet.registries, attributes }).length > 0;
}
const $aliasTemplateDeclaresChildren = aliasTemplateDeclaresChildren;

/**
 * Operations over alias templates: expanding an instance and resolving its
 * props and dynamic children.
 */
export namespace AliasTemplateOps {
  export const expandInstance: typeof $expandAliasInstance = $expandAliasInstance;
  export const prepareExpansion: typeof $prepareAliasExpansion = $prepareAliasExpansion;
  export const localState: typeof $aliasLocalState = $aliasLocalState;
  export const resolveDynamicTailSourceKey: typeof $resolveDynamicTailSourceKey = $resolveDynamicTailSourceKey;
  export const resolveInitialProps: typeof $resolveInitialAliasProps = $resolveInitialAliasProps;
  export const expandChildSpecsRecursive: typeof $expandChildSpecsRecursive = $expandChildSpecsRecursive;
  export const expansionSignature: typeof $aliasExpansionSignature = $aliasExpansionSignature;
  /** Derived-child-spec (OUTPUT) signature — see `aliasChildSpecSignature`. */
  export const childSpecSignature: typeof $aliasChildSpecSignature = $aliasChildSpecSignature;
  export const isSyntheticChild: typeof $isSyntheticAliasChild = $isSyntheticAliasChild;
  export const owningAttachedAlias: typeof $owningAttachedAlias = $owningAttachedAlias;
  export const displayName: typeof $aliasTemplateDisplayName = $aliasTemplateDisplayName;
  /** Resolve an alias template's declared default data address (`DataRef`). */
  export const dataRef: typeof $aliasDataRef = $aliasDataRef;
  /** Whether an alias's template declares child rows (static or a dynamic tail). */
  export const declaresChildren: typeof $aliasTemplateDeclaresChildren = $aliasTemplateDeclaresChildren;
}
